import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Pencil, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AdminHeader } from "@/components/admin/AdminShared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { STATUS_COLORS, formatDateShort } from "@/lib/helpers";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUSES = ["Semua", "Draft", "Review", "Approved", "Published"];

export default function NewsAdmin() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("Semua");
  const [delId, setDelId] = useState(null);

  const load = () => {
    const p = new URLSearchParams({ admin: true, limit: 100 });
    if (search) p.set("search", search);
    if (status !== "Semua") p.set("status", status);
    api.get(`/news?${p}`).then((r) => setItems(r.data.items));
  };
  useEffect(() => { load(); }, [status]);

  const del = async () => {
    await api.delete(`/news/${delId}`);
    toast.success("Berita dihapus");
    setDelId(null); load();
  };

  return (
    <div>
      <AdminHeader title="Manajemen Berita" description="Kelola berita dan publikasi dengan alur verifikasi."
        action={<Button onClick={() => navigate("/admin/news/create")} className="bg-brandred hover:bg-brandred-700 text-white" data-testid="add-news-btn"><Plus className="h-4 w-4 mr-1.5" /> Tambah Berita</Button>} />

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-1 gap-2">
          <div className="flex items-center flex-1 bg-white border border-slate-200 rounded-lg px-3">
            <Search className="h-4 w-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari berita..." className="border-0 shadow-none focus-visible:ring-0" data-testid="news-search" />
          </div>
          <Button type="submit" className="bg-navy text-white">Cari</Button>
        </form>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-44" data-testid="news-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden overflow-x-auto" data-testid="cms-news-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-surface">
              <TableHead>Judul</TableHead>
              <TableHead>Kategori</TableHead>
              <TableHead>Tanggal</TableHead>
              <TableHead>Penulis</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((n) => (
              <TableRow key={n.id} data-testid={`news-row-${n.id}`}>
                <TableCell className="font-semibold text-navy max-w-xs"><span className="line-clamp-1">{n.title}</span></TableCell>
                <TableCell className="text-sm text-slate-500">{n.category}</TableCell>
                <TableCell className="text-sm text-slate-500">{formatDateShort(n.date)}</TableCell>
                <TableCell className="text-sm text-slate-500">{n.author}</TableCell>
                <TableCell><span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${STATUS_COLORS[n.status]}`}>{n.status}</span></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => window.open(`/info/${n.slug}`, "_blank")}><Eye className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => navigate(`/admin/news/${n.id}`)} data-testid={`edit-news-${n.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => setDelId(n.id)} data-testid={`delete-news-${n.id}`} className="text-brandred hover:text-brandred"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">Tidak ada berita.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus berita ini?</AlertDialogTitle>
            <AlertDialogDescription>Tindakan ini tidak dapat dibatalkan. Berita akan dihapus permanen.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-brandred hover:bg-brandred-700" data-testid="confirm-delete">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
